import { useEffect, useRef, useState } from 'react';
import { adsenseApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import type { AdsenseConfig } from '../types';

// Kill-switch global de build-time: quando VITE_ADS_ENABLED === 'false', NENHUM
// AdSlot renderiza nem chama a API. Usado durante a janela de reavaliação do
// Google AdSense (conteúdo "thin" + ads ativos = sinal ruim pro crawler).
// Default = true; só desliga quando explicitamente definido como 'false'.
const ADS_ENABLED = import.meta.env.VITE_ADS_ENABLED !== 'false';

/**
 * Cache separado por área (app vs public) para evitar múltiplas requests.
 */
const cache: Record<string, { config: AdsenseConfig | null; promise: Promise<AdsenseConfig> | null }> = {
  app: { config: null, promise: null },
  public: { config: null, promise: null },
};

function getAdsenseConfig(area: 'app' | 'public'): Promise<AdsenseConfig> {
  const entry = cache[area];
  if (entry.config) return Promise.resolve(entry.config);
  if (!entry.promise) {
    const fetcher = area === 'public' ? adsenseApi.getPublicConfig : adsenseApi.getConfig;
    entry.promise = fetcher().then((c) => {
      entry.config = c;
      return c;
    });
  }
  return entry.promise;
}

interface AdSlotProps {
  slotName: string;
  format?: 'auto' | 'horizontal' | 'rectangle' | 'vertical';
  className?: string;
  /** Se true, o usuário é premium e não deve ver anúncios */
  isPremium?: boolean;
  /** Se true, o status de premium ainda está carregando — não renderiza nada para evitar flash */
  isLoadingPremium?: boolean;
  /** Área do anúncio: 'app' (área logada, default) ou 'public' (landing pages) */
  area?: 'app' | 'public';
}

/**
 * Componente de slot de anúncio AdSense.
 * Renderiza o `<ins>` do AdSense apenas se:
 * 1. AdSense está habilitado globalmente (da área correspondente)
 * 2. O slot específico está habilitado
 * 3. O usuário não é premium
 */
export function AdSlot({ slotName, format = 'auto', className = '', isPremium = false, isLoadingPremium = false, area = 'app' }: AdSlotProps) {
  const { user } = useAuth();
  const [config, setConfig] = useState<AdsenseConfig | null>(null);
  const adRef = useRef<HTMLModElement>(null);
  const loaded = useRef(false);

  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';

  useEffect(() => {
    if (!ADS_ENABLED) return;
    getAdsenseConfig(area).then(setConfig).catch(() => {});
  }, [area]);

  useEffect(() => {
    if (!ADS_ENABLED) return;
    if (!config || isPremium) return;
    if (area === 'app' && isAdmin && !config.showForAdmins) return;
    if (!config.enabled) return;

    const slot = config.slots[slotName];
    if (!slot?.enabled || !slot?.unitId) return;

    // Injetar script global do AdSense se não injetado ainda
    if (config.globalCode && !document.querySelector('script[data-adsense]')) {
      const script = document.createElement('script');
      script.setAttribute('data-adsense', 'true');
      script.async = true;
      script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${config.globalCode}`;
      script.crossOrigin = 'anonymous';
      document.head.appendChild(script);
    }

    // Acionar push do AdSense (apenas uma vez por slot)
    if (!loaded.current && adRef.current) {
      loaded.current = true;
      try {
        // @ts-ignore
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch {}
    }
  }, [config, slotName, isPremium]);

  if (!ADS_ENABLED) return null;
  if (!config || isLoadingPremium || isPremium || !config.enabled) return null;
  if (area === 'app' && isAdmin && !config.showForAdmins) return null;

  const slot = config.slots[slotName];
  if (!slot?.enabled) return null;

  // Modo teste: exibe placeholder visual vermelho
  if (config.testMode) {
    return (
      <div className={`adslot-container ${className}`}>
        <div className="bg-red-500/90 text-white p-3 text-center rounded border-2 border-red-600 border-dashed">
          <div className="text-xs font-mono opacity-75 mb-1">ÁREA DE ANÚNCIO {area === 'public' ? '(PÚBLICO)' : ''}</div>
          <div className="text-sm font-bold">{slotName}</div>
          <div className="text-xs opacity-75">{format}</div>
        </div>
      </div>
    );
  }

  if (!slot?.unitId) return null;

  return (
    <div className={`adslot-container ${className}`}>
      <ins
        ref={adRef}
        className="adsbygoogle"
        style={{ display: 'block' }}
        data-ad-client={config.globalCode}
        data-ad-slot={slot.unitId}
        data-ad-format={format}
        data-full-width-responsive="true"
      />
    </div>
  );
}

export default AdSlot;
